import React, { useCallback, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PRESET_AMOUNTS = [10, 25, 50];

export default function Donate() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const complete = params.get('session_id') != null;
  const [selectedAmount, setSelectedAmount] = useState(PRESET_AMOUNTS[1]);
  const [customAmount, setCustomAmount] = useState('');
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [error, setError] = useState(null);

  const dollarAmount = customAmount ? Number(customAmount) : selectedAmount;
  const amountValid = Number.isFinite(dollarAmount) && dollarAmount >= 1 && dollarAmount <= 1000;

  const fetchClientSecret = useCallback(async () => {
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Math.round(dollarAmount * 100) }),
      });

      if (!response.ok) {
        const { error: message } = await response.json().catch(() => ({}));
        throw new Error(message || 'Failed to start checkout');
      }

      const { clientSecret } = await response.json();
      return clientSecret;
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
      setCheckoutStarted(false);
      throw err;
    }
  }, [dollarAmount]);

  const handleStartCheckout = () => {
    if (!amountValid) return;
    setError(null);
    setCheckoutStarted(true);
  };

  if (complete) {
    return (
      <section className="page philosophy-page">
        <header className="section-header centered serif">
          <h1 className="title">Thank You</h1>
          <p>Your donation means a lot to us — thank you for supporting Dojo of Pain.</p>
          <p className="donate-ein">
            Dojo of Pain is a tax-exempt organization under IRC Section 501(c)(3). EIN: 99-4019031.
          </p>
        </header>
      </section>
    );
  }

  return (
    <section className="page philosophy-page">
      <header className="section-header centered serif">
        <h1 className="title">Donate</h1>
        <p>
          Free membership is core to Dojo's values. If you attend a practice, finish a long run,
          or crush an 8-mile aerobic workout, you're one of us. But extra funds are always
          helpful for improving club operations.
        </p>
        <p>
          The board will direct all funds back to club members in the form of merchandise
          purchases, event planning, and post-long run snacks.
        </p>
        <p>Please reach out if you have any questions.</p>
      </header>

      {!checkoutStarted && (
        <div className="donate-amount-picker">
          <div className="donate-amount-options">
            {PRESET_AMOUNTS.map((amount) => (
              <button
                key={amount}
                type="button"
                className={`donate-amount-button ${!customAmount && selectedAmount === amount ? 'active' : ''}`}
                onClick={() => {
                  setSelectedAmount(amount);
                  setCustomAmount('');
                }}
              >
                ${amount}
              </button>
            ))}
            <div className={`donate-amount-custom ${customAmount ? 'active' : ''}`}>
              <span>$</span>
              <input
                type="number"
                min="1"
                max="1000"
                placeholder="Other"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
            </div>
          </div>

          {!amountValid && (
            <p className="donate-amount-error">Enter an amount between $1 and $1,000.</p>
          )}

          <button
            type="button"
            className="social-cta"
            disabled={!amountValid}
            onClick={handleStartCheckout}
          >
            Donate ${amountValid ? dollarAmount : ''}
          </button>
        </div>
      )}

      {error && <p className="donate-amount-error">{error}</p>}

      {checkoutStarted && (
        <div className="donate-checkout-embed">
          <EmbeddedCheckoutProvider
            key={dollarAmount}
            stripe={stripePromise}
            options={{ fetchClientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}
    </section>
  );
}
